import { Spinner } from "@blueprintjs/core";
import {
  useGetPostListFullQuery,
  type Post,
} from "@package/ui-gql/index-data-access";
import { useWallProvider } from "@package/ui-wall";
import { MediaDisplay } from "@package/ui-wall-components";
import { uniqBy } from "lodash";
import { useEffect, useState } from "react";
import { type FlyoutProps } from "../../utils/flyout-props";

type RelatedPostProps = {
  screen: FlyoutProps["screen"];
  post: Post;
};

function RelatedPost({ post, screen }: RelatedPostProps) {
  const provider = useWallProvider();

  return (
    <div
      className="flex-none relative h-44 pr-4"
      onPointerDown={() =>
        post.id && provider?.onPostFlyoutDirectActivation(screen.id, post.id)
      }
    >
      <MediaDisplay id={post.relatedPostAsset ?? undefined} forceFit />
    </div>
  );
}

type FlyoutEventRelatedProps = {
  screen: FlyoutProps["screen"];
  limit?: number;
};

export function FlyoutRelatedPost({
  limit = 5,
  screen,
}: FlyoutEventRelatedProps) {
  const [relatedPosts, setRelatedPosts] = useState<Post[]>(
    screen.flyout?.relatedPosts ?? [],
  );

  const { data: dataTags, loading: loadingTags } = useGetPostListFullQuery({
    variables: {
      filter: {
        enabled: true,
        hideFromSearch: false,
        tags: screen.flyout?.post?.tags ?? [],
      },
      page: 0,
      perPage: limit - (screen.flyout?.relatedPosts ?? []).length,
      sample: true,
    },
    skip:
      (screen.flyout?.relatedPosts ?? []).length > limit ||
      (screen.flyout?.post?.tags ?? []).length === 0,
  });

  useEffect(
    function effectOnRelatedPostFlyoutChanged() {
      const postsToUpdate = (screen.flyout?.relatedPosts ?? []).concat(
        dataTags?.items ?? [],
      );

      setRelatedPosts(uniqBy(postsToUpdate, (p) => p.id).slice(0, limit));
    },
    [dataTags?.items, limit, screen.flyout?.relatedPosts],
  );

  if (loadingTags) {
    return (
      <div className="flex flex-row w-full m-4 h-44 bg-black/25 overflow-hidden">
        <Spinner size={64} />
      </div>
    );
  }

  if (relatedPosts.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-row w-full p-4 bg-black/25 overflow-hidden">
      {relatedPosts
        .filter((post) => post.id && post.relatedPostAsset)
        .slice(0, limit)
        .map((post) => (
          <RelatedPost key={post.id} post={post} screen={screen} />
        ))}
    </div>
  );
}
